import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../api";
import { createAsyncThunkInstance } from "../createAsyncThunkInstance";
import { ICommonInitialState } from "../../shared/types/storeTypes";
import { AxiosError } from "axios";
import {
  ProjectsRequest,
  ProjectsResponse,
  ProjectResponse,
  ProjectRequest,
  ProjectCreateRequest,
  ProjectCreateResponse,
  ProjectUpdateRequest,
  ProjectUpdateResponse,
} from "../../api/projects/types";

interface IProjectsState extends ICommonInitialState {
  projects: ProjectsResponse;
  currentProject: ProjectResponse | null;
}

const initialState: IProjectsState = {
  projects: {
    count: 0,
    items: [],
    can_add: false,
  },
  currentProject: null,
  isLoading: false,
  error: null,
};

export const fetchAllProjects = createAsyncThunkInstance<ProjectsResponse, ProjectsRequest | undefined>(
  "projects/fetchAllProjects",
  async (data?: ProjectsRequest) => {
    const response = await api.projects.projects({
      params: { filters: data?.params.filters, expand: ["customer", "status", "responsible_user", "created_at", "created_by", "legal_entity"] },
    });
    return response;
  },
);

export const fetchSingleProject = createAsyncThunkInstance<ProjectResponse, ProjectRequest>("projects/fetchSingleProject", api.projects.projectView);

export const fetchCreateProject = createAsyncThunk<ProjectCreateResponse, ProjectCreateRequest, { rejectValue: AxiosError }>(
  "projects/fetchCreateProject",
  async (params: ProjectCreateRequest, { rejectWithValue }) => {
    try {
      const { data } = await api.projects.projectCreate(params);
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  },
);

export const fetchUpdateProject = createAsyncThunk<ProjectUpdateResponse, ProjectUpdateRequest, { rejectValue: AxiosError }>(
  "projects/fetchUpdateProject",
  async (params: ProjectUpdateRequest, { rejectWithValue }) => {
    try {
      const { data } = await api.projects.updateProject({ id: params.id, body: params.body });
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  },
);

export const fetchDeleteProject = createAsyncThunkInstance<undefined, number>("projects/fetchDeleteProject", api.projects.deleteProject);

const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    clearCurrentProject: (state) => {
      state.currentProject = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllProjects.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllProjects.fulfilled, (state, action) => {
        state.isLoading = false;
        state.projects = action.payload;
      })
      .addCase(fetchSingleProject.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleProject.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProject = action.payload;
      });
  },
});

export const { clearCurrentProject } = projectsSlice.actions;
export const projectsReducer = projectsSlice.reducer;
